import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useVoucher } from '@quizzach/api-hooks';
import { CardAuthorMark, Navigation } from '@quizzach/components';
import {
	BtnLine,
	BtnTeal,
	Hand,
	VPage,
	VoucherBigGift,
	VoucherCard,
	VoucherCf,
	VoucherCfq,
	VoucherFoot,
	VoucherGiant,
	VoucherHoles,
	VoucherStage,
	VoucherTicket,
	VoucherTicketMain,
	VoucherWon,
} from '@quizzach/styles';
import { LogoWordmark, QLogo, Xcollab } from '@quizzach/assets';
import { RoutePath } from '@quizzach/interfaces';
import { scrollToTop } from '@quizzach/utils';

const STANDARD_COVER = '/quiz-play-backgrounds/default_play_cover.webp';

const QP1 =
	'M83.3695 28.9437C84.8699 21.2143 83.6923 15.2555 79.8366 11.0672C75.9988 6.78694 68.7549 3.61033 58.1049 1.53737C46.9041 -0.642818 38.5403 -0.504165 33.0134 1.95332C27.5963 4.33667 24.1375 9.39304 22.6371 17.1224L17.8412 41.8289C17.0195 46.0617 16.7998 49.6477 17.182 52.5869C17.5464 55.6181 18.3023 58.1049 19.4498 60.0471C20.5972 61.9893 22.3211 63.6618 24.6213 65.0644C26.8477 66.3572 29.1735 67.3829 31.5987 68.1414C32.9955 68.5783 34.6904 69.0257 36.6836 69.4836C36.9726 69.55 37.7426 69.7186 38.0891 69.7818L41.2589 70.3988C48.2376 71.7572 44.1173 61.873 41.9526 61.4516L39.3263 60.9346C37.5642 60.4245 36.0827 59.8885 34.8819 59.3266C32.546 58.108 31.0057 56.4713 30.2613 54.4165C29.5168 52.3617 29.5107 49.448 30.243 45.6753L34.9854 21.2449C35.8428 16.8281 36.8262 13.725 37.9358 11.9356C39.1372 10.1641 40.994 9.18855 43.5061 9.00906C46.11 8.84745 50.258 9.32062 55.9503 10.4286C60.8162 11.3757 64.3713 12.4497 66.6154 13.6504C68.8775 14.7592 70.3169 16.424 70.9339 18.645C71.5687 20.7739 71.4753 23.9547 70.6536 28.1875L62.0826 72.3655L62.08 72.3787L61.475 75.4954C60.87 78.6121 71.6336 89.4019 73.5841 79.354L83.3695 28.9437Z';
const QP2 =
	'M70.1815 97.0626C69.7997 99.0298 67.899 100.315 65.9362 99.9328L60.6541 98.9046C58.6913 98.5226 57.4097 96.6182 57.7916 94.651L58.6775 90.0872C60.628 80.0393 71.3915 90.8292 70.7865 93.9459L70.1815 97.0626Z';

const CONFETTI_COLORS = ['var(--teal)', 'var(--coral)', 'var(--gold)'];

/* Q simboli koji lete iz kutije: mali roj na inline ikoni, veći na
   velikoj kutiji (s vlastitim dometom --ry) */
const QF_SMALL = [
	{ fx: '-52px', rr: '-280deg', color: 'var(--teal)', delay: '0s', w: 18 },
	{ fx: '44px', rr: '260deg', color: 'var(--coral)', delay: '0.5s', w: 15 },
	{ fx: '-18px', rr: '-200deg', color: 'var(--gold)', delay: '1s', w: 21 },
	{ fx: '24px', rr: '320deg', color: 'var(--teal)', delay: '1.5s', w: 14 },
	{ fx: '60px', rr: '220deg', color: 'var(--gold)', delay: '1.9s', w: 17 },
];

const QF_BIG = [
	{
		fx: '-110px',
		ry: '-150px',
		rr: '-300deg',
		color: 'var(--teal)',
		delay: '0s',
		w: 18,
	},
	{
		fx: '96px',
		ry: '-170px',
		rr: '280deg',
		color: 'var(--coral)',
		delay: '0.25s',
		w: 15,
	},
	{
		fx: '-40px',
		ry: '-180px',
		rr: '-220deg',
		color: 'var(--gold)',
		delay: '0.5s',
		w: 22,
	},
	{
		fx: '40px',
		ry: '-140px',
		rr: '320deg',
		color: 'var(--teal)',
		delay: '0.75s',
		w: 13,
	},
	{
		fx: '130px',
		ry: '-120px',
		rr: '240deg',
		color: 'var(--gold)',
		delay: '0.95s',
		w: 16,
	},
	{
		fx: '-140px',
		ry: '-110px',
		rr: '-240deg',
		color: 'var(--coral)',
		delay: '1.15s',
		w: 14,
	},
];

type Phase = 'entry' | 'go' | 'reveal' | 'done';

const GiftIcon = ({
	size = 38,
	className,
}: {
	size?: number;
	className?: string;
}) => (
	<svg
		className={className}
		width={size}
		height={size}
		viewBox='0 0 42 42'
		fill='none'
	>
		<path
			d='M11.5 2.5c1.3-1.4 3.5-1.4 4.9 0l2 2.1c-2 2.2-2.2 5.4-.7 7.9-.8 0-1.7-.4-2.3-1L11.5 7.5c-1.4-1.4-1.4-3.6 0-5Zm13.1 0c1.3-1.4 3.5-1.4 4.9 0 1.3 1.4 1.3 3.6 0 5l-3.9 4c-.7.6-1.5 1-2.4 1 1.6-2.5 1.4-5.7-.6-7.9l2-2.1ZM20.5 6.7c1 1.3 1 3.2 0 4.5-1-1.3-1-3.2 0-4.5Z'
			stroke='currentColor'
			strokeWidth='3'
		/>
		<path
			d='M2 21c0-1.7 1.3-3 3-3h13c.6 0 1 .4 1 1v22c0 .6-.4 1-1 1H5c-1.7 0-3-1.3-3-3V21Z'
			fill='currentColor'
		/>
		<path
			d='M0 10c0-1.7 1.3-3 3-3h36c1.7 0 3 1.3 3 3v3c0 1.7-1.3 3-3 3H3c-1.7 0-3-1.3-3-3v-3Z'
			fill='currentColor'
		/>
		<path
			d='M40 21c0-1.7-1.3-3-3-3H24c-.6 0-1 .4-1 1v22c0 .6.4 1 1 1h13c1.7 0 3-1.3 3-3V21Z'
			fill='currentColor'
		/>
	</svg>
);

const QGlyph = ({ width }: { width: number }) => (
	<svg
		width={width}
		height={Math.round(width * 1.47)}
		viewBox='0 0 104 153'
		fill='none'
	>
		<path d={QP1} fill='currentColor' />
		<path d={QP2} fill='currentColor' />
	</svg>
);

const Confetti = () => {
	const pieces = useMemo(
		() => ({
			squares: Array.from({ length: 34 }, (_, i) => ({
				left: 2 + Math.random() * 94,
				size: 6 + Math.random() * 9,
				color: CONFETTI_COLORS[i % 3],
				delay: i * 0.035,
				dx: Math.random() * 240 - 120,
			})),
			logos: Array.from({ length: 14 }, (_, j) => ({
				left: 4 + Math.random() * 88,
				size: 16 + Math.random() * 18,
				color: CONFETTI_COLORS[j % 3],
				delay: 0.05 + j * 0.06,
				dx: Math.random() * 260 - 130,
			})),
		}),
		[]
	);

	return (
		<>
			{pieces.squares.map((p, i) => (
				<VoucherCf
					key={`c${i}`}
					style={
						{
							left: `${p.left}%`,
							width: p.size,
							height: p.size,
							background: p.color,
							animationDelay: `${p.delay}s`,
							'--dx': `${p.dx}px`,
						} as React.CSSProperties
					}
				/>
			))}
			{pieces.logos.map((p, j) => (
				<VoucherCfq
					key={`q${j}`}
					style={
						{
							left: `${p.left}%`,
							width: p.size,
							height: p.size,
							animationDelay: `${p.delay}s`,
							'--dx': `${p.dx}px`,
						} as React.CSSProperties
					}
				>
					<svg viewBox='0 0 100 100' fill='none'>
						<path d={QP1} fill={p.color} />
						<path d={QP2} fill={p.color} />
					</svg>
				</VoucherCfq>
			))}
		</>
	);
};

export const UserVoucher = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		code,
		error,
		loading,
		quiz,
		errorMessage,
		setError,
		handleSubmit,
		handleInputChange,
	} = useVoucher();
	const [submitted, setSubmitted] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [phase, setPhase] = useState<Phase>('entry');

	const giftRef = useRef<HTMLDivElement>(null);
	const slotRef = useRef<HTMLSpanElement>(null);
	const wonRef = useRef<HTMLDivElement>(null);
	const timers = useRef<number[]>([]);

	useEffect(() => {
		scrollToTop();
	}, []);

	useEffect(() => () => timers.current.forEach(clearTimeout), []);

	// success needs an actual quiz — clearing an error must never flip
	// a stale `submitted` into the success state
	const redeemed = submitted && !error && !loading && Boolean(quiz.uuid);

	/* koreografija otvaranja: go (kutija iskoči s prskom Q-ova) →
	   reveal (karta se ispuni, kutija putuje na svoje mjesto) → done
	   (inline ikona preuzme). Cilj leta se mjeri iz DOM-a u konačnoj
	   veličini karte — rect u međustanju krosfejda bio bi kriv. */
	useEffect(() => {
		if (!redeemed || phase !== 'entry') {
			return;
		}
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			setPhase('done');
			return;
		}
		setPhase('go');
		timers.current.push(
			window.setTimeout(() => {
				const gift = giftRef.current;
				const slot = slotRef.current;
				const won = wonRef.current;
				if (gift && slot && won) {
					won.style.transition = 'none';
					won.style.transform = 'none';
					const sr = slot.getBoundingClientRect();
					const gr = gift.getBoundingClientRect();
					won.style.transform = '';
					won.style.transition = '';
					gift.style.setProperty(
						'--tx',
						`${sr.left + sr.width / 2 - (gr.left + gr.width / 2)}px`
					);
					gift.style.setProperty(
						'--ty',
						`${sr.top + sr.height / 2 - (gr.top + gr.height / 2)}px`
					);
				}
				setPhase('reveal');
				timers.current.push(window.setTimeout(() => setPhase('done'), 1550));
			}, 1500)
		);
	}, [redeemed, phase]);

	const onSubmit = async (e: React.FormEvent) => {
		await handleSubmit(e);
		setSubmitted(true);
		// remount the form on every try so the shake animation restarts
		setAttempt((a) => a + 1);
	};

	const onChange = (value: string) => {
		if (error) {
			setError(false);
		}
		setSubmitted(false);
		handleInputChange(value);
	};

	const isPremade = quiz.quiz_type === 'premade';
	const cover = isPremade
		? `${import.meta.env.VITE_ASSETS_URL}/thematic_quizzes/${quiz.premade_quiz
				?.cover_photo_small}`
		: STANDARD_COVER;
	const wonTitle = isPremade
		? quiz.premade_quiz?.title
		: quiz.quiz_type === 'generator'
			? t('profile.quizCard.configured')
			: t('profile.quizCard.generalKnowledge');
	const authorName = quiz.premade_quiz?.author?.display_name;
	const showAuthor = isPremade && !!authorName && authorName !== 'quizzach';
	/* error responses can land in `quiz` too (useVoucher setQuiz-a i
	   error body), pa uuid ovdje ne smije biti preduvjet renderiranja */
	const shortId = quiz.uuid ? quiz.uuid.substring(quiz.uuid.length - 8) : '';
	const startQuiz = () => navigate(`/quiz/${quiz.uuid}/${RoutePath.QuizPlay}`);

	return (
		<VPage data-id='voucher-container'>
			<Navigation />
			<VoucherStage data-phase={phase}>
				<VoucherGiant aria-hidden>
					<span className='g1'>{t('profile.voucher.giantA')}</span>
					<span className='g2'>{t('profile.voucher.giantB')}</span>
				</VoucherGiant>
				<VoucherCard $busy={phase !== 'entry'}>
					<VoucherTicket $hide={phase !== 'entry'} data-id='user-voucher'>
						<VoucherTicketMain>
							<Hand $color='coral' $rotate={-2}>
								{t('profile.voucher.forYou')}
							</Hand>
							<h2>{t('profile.voucher.title')}</h2>
							<p>{t('profile.voucher.description')}</p>
							<form
								key={attempt}
								className={error ? 'shake' : ''}
								onSubmit={onSubmit}
							>
								<input
									placeholder={t('profile.voucher.enterCode')}
									name='code-input'
									autoComplete='off'
									className={error ? 'bad' : ''}
									value={code}
									onChange={(e) => onChange(e.target.value)}
								/>
								<BtnTeal type='submit' disabled={code.length === 0 || loading}>
									{t('profile.voucher.apply')}
								</BtnTeal>
							</form>
							{error && (
								<div className='terr' role='alert'>
									<svg
										width='18'
										height='18'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									>
										<circle cx='12' cy='12' r='10' />
										<path d='M12 8v4M12 16h.01' />
									</svg>
									<span>{errorMessage}</span>
								</div>
							)}
						</VoucherTicketMain>
						<VoucherHoles aria-hidden />
						<VoucherFoot>
							<span className='flab'>{t('profile.voucher.title')}</span>
							<div className='bars' aria-hidden />
						</VoucherFoot>
					</VoucherTicket>
					<VoucherWon
						ref={wonRef}
						$show={phase === 'reveal' || phase === 'done'}
						$done={phase === 'done'}
						data-id='user-voucher-won'
					>
						<div className='ph'>
							{quiz.uuid && (
								<img
									className='bg'
									src={cover}
									alt={wonTitle ?? ''}
									data-darkzone
								/>
							)}
							{showAuthor && authorName ? (
								<span
									className='amark'
									aria-label={t('cart.quizType.byAuthor', {
										author: authorName,
									})}
								>
									<QLogo size={22} />
									<Xcollab size={11} />
									<CardAuthorMark displayName={authorName} />
								</span>
							) : (
								<span className='amark' aria-label='quizzach'>
									<LogoWordmark width={72} height={22} />
								</span>
							)}
						</div>
						<div className='main'>
							<div className='ok'>
								<span className='gift-pop' ref={slotRef}>
									{QF_SMALL.map((q, i) => (
										<span
											key={i}
											className='qf'
											style={
												{
													'--fx': q.fx,
													'--rr': q.rr,
													color: q.color,
													animationDelay: q.delay,
												} as React.CSSProperties
											}
										>
											<QGlyph width={q.w} />
										</span>
									))}
									<GiftIcon size={38} />
								</span>
							</div>
							<div className='tt'>
								<h3>{wonTitle}</h3>
								<span className='im'>
									{t('profile.quizCard.questionsShort', {
										num: Math.max(0, quiz.number_of_questions - 1),
									})}
								</span>
							</div>
						</div>
						<VoucherHoles aria-hidden />
						<div className='stub'>
							<div className='bc'>
								<div className='bars' aria-hidden />
								<span className='code'>
									{t('profile.quizzes.shortId', { id: shortId })}
								</span>
							</div>
							<div className='acts'>
								<BtnTeal $small type='button' onClick={startQuiz}>
									<svg width='14' height='14' viewBox='0 0 24 24'>
										<path d='M8 5v14l11-7z' fill='currentColor' />
									</svg>
									{t('profile.quizCard.start')}
								</BtnTeal>
								<BtnLine
									$small
									type='button'
									className='collect'
									onClick={() => navigate(RoutePath.Profile)}
								>
									{t('profile.voucher.toCollection')}
								</BtnLine>
							</div>
						</div>
						{(phase === 'reveal' || phase === 'done') && <Confetti />}
					</VoucherWon>
				</VoucherCard>
				<VoucherBigGift ref={giftRef} $phase={phase} aria-hidden>
					{QF_BIG.map((q, i) => (
						<span
							key={i}
							className='qf'
							style={
								{
									'--fx': q.fx,
									'--ry': q.ry,
									'--rr': q.rr,
									color: q.color,
									animationDelay: q.delay,
								} as React.CSSProperties
							}
						>
							<QGlyph width={q.w} />
						</span>
					))}
					<GiftIcon size={110} className='box' />
				</VoucherBigGift>
			</VoucherStage>
		</VPage>
	);
};
